import { Alert, Button, Card } from '@safepos/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';

interface Status {
  mode: 'fnb' | 'retail';
  steps: { id: string; title: string; hint: string; done: boolean; to: string }[];
  completed: number;
  total: number;
  demo: { count: number; canAdd: boolean };
}

const KEY = 'admin.onboarding.dismissed';
const dismissed = () => {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

/** Panduan memulai di dasbor: langkah dihitung dari data nyata; data contoh bisa ditambah/dihapus. */
export function OnboardingCard() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [hidden, setHidden] = useState(dismissed);
  const q = useQuery({
    queryKey: ['onboarding'],
    enabled: can('catalog:read') && !hidden,
    queryFn: () => api.get<Status>('/onboarding'),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['onboarding'] });
    void qc.invalidateQueries({ queryKey: ['catalog-summary'] });
    void qc.invalidateQueries({ queryKey: ['products'] });
  };
  const add = useSubmit(async () => {
    await api.post('/onboarding/demo');
    refresh();
  });
  const remove = useSubmit(async () => {
    if (!confirm('Hapus semua produk contoh?')) return;
    await api.del('/onboarding/demo');
    refresh();
  });
  const s = q.data;
  if (hidden || !s) return null;
  const allDone = s.completed === s.total;
  const pct = Math.round((s.completed / s.total) * 100);
  const write = can('catalog:write');
  const hide = () => {
    try {
      localStorage.setItem(KEY, '1');
    } catch {
      /* penyimpanan tidak tersedia */
    }
    setHidden(true);
  };
  return (
    <div className="mb-4">
      <Card
        title={allDone ? 'Semua langkah awal selesai 🎉' : 'Mulai berjualan dengan SafePOS'}
        actions={
          <Button variant="ghost" onClick={hide}>
            Sembunyikan
          </Button>
        }
      >
        <div className="mb-3 flex items-center gap-3">
          <div
            className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Kemajuan langkah awal"
          >
            <div className="h-full bg-brand-600" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-sm text-slate-600">
            {s.completed}/{s.total}
          </span>
        </div>
        {(add.error || remove.error) && <Alert>{add.error ?? remove.error}</Alert>}
        <ol className="divide-y">
          {s.steps.map((st, i) => (
            <li key={st.id} className="flex items-start gap-3 py-2">
              <span
                aria-hidden="true"
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${st.done ? 'bg-green-700 text-white' : 'bg-slate-200 text-slate-600'}`}
              >
                {st.done ? '✓' : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className={st.done ? 'text-slate-500 line-through' : 'font-medium'}>
                  {st.title}
                </div>
                {!st.done && <div className="text-sm text-slate-600">{st.hint}</div>}
              </div>
              {!st.done && (
                <Link
                  to={st.to}
                  className="whitespace-nowrap text-sm font-medium text-brand-700 underline"
                >
                  Buka
                </Link>
              )}
            </li>
          ))}
        </ol>
        {write && (s.demo.canAdd || s.demo.count > 0) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-3 text-sm">
            {s.demo.canAdd ? (
              <>
                <span className="flex-1 text-slate-600">
                  Ingin mencoba dulu? Tambahkan {s.mode === 'fnb' ? 'menu restoran' : 'produk toko'}{' '}
                  contoh (bisa dihapus kapan saja).
                </span>
                <Button variant="secondary" disabled={add.busy} onClick={() => void add.run()}>
                  Tambah data contoh
                </Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-slate-600">
                  Ada {s.demo.count} produk contoh di katalog.
                </span>
                <Button
                  variant="secondary"
                  disabled={remove.busy}
                  onClick={() => void remove.run()}
                >
                  Hapus data contoh
                </Button>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
